import { contactInfo } from '@/data/contact'
import { normalizeTurkishPhoneDigits } from '@/utils/phone'
import { SITE_URL, SITE_NAME, absUrl } from '@/config/site'
import { neighborhoods } from '@/data/neighborhoods'

const ORG_ID = `${SITE_URL}/#organization`
const WEBSITE_ID = `${SITE_URL}/#website`

const telephone = `+${normalizeTurkishPhoneDigits(contactInfo.phone)}`

const postalAddress = {
  "@type": "PostalAddress",
  "streetAddress": "Fuar Alanı Kavşağı Dış Kapı:2, Batıkent Mahallesi Çardaklıkaya Bulvarı, C İç Kapı: No:22",
  "addressLocality": "Karaköprü",
  "addressRegion": "Şanlıurfa",
  "postalCode": "63000",
  "addressCountry": "TR"
}

// Koordinat, klinik adresinin (Çardaklıkaya Bulvarı, Batıkent, Karaköprü)
// OpenStreetMap üzerinden doğrulanmış konumudur.
const geo = {
  "@type": "GeoCoordinates",
  "latitude": 37.2099,
  "longitude": 38.7912
}

const openingHoursSpecification = [
  {
    "@type": "OpeningHoursSpecification",
    "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    "opens": "09:00",
    "closes": "19:00"
  },
  {
    "@type": "OpeningHoursSpecification",
    "dayOfWeek": ["Saturday", "Sunday"],
    "opens": "09:00",
    "closes": "17:00"
  }
]

/**
 * Klinik/kuruluş kimliği. VeterinaryCare zaten LocalBusiness alt tipidir;
 * bu yüzden ayrı bir LocalBusiness nesnesi yerine tek @id üzerinden tanımlanır.
 */
export const organizationData = {
  "@type": ["VeterinaryCare", "MedicalBusiness"],
  "@id": ORG_ID,
  "name": SITE_NAME,
  "alternateName": "Ardıç Veteriner",
  "description": "Şanlıurfa Karaköprü'de modern donanımlı veteriner kliniği. Kedi ve köpek sağlığı için 7/24 acil hizmet, cerrahi, görüntüleme ve laboratuvar.",
  "url": SITE_URL,
  "telephone": telephone,
  "email": contactInfo.email,
  "address": postalAddress,
  "geo": geo,
  "openingHoursSpecification": openingHoursSpecification,
  "hasMap": "https://maps.google.com/?q=Ardıç+Veteriner+Kliniği+Karaköprü+Şanlıurfa",
  "areaServed": [
    { "@type": "City", "name": "Şanlıurfa" },
    { "@type": "AdministrativeArea", "name": "Karaköprü" },
    { "@type": "AdministrativeArea", "name": "Haliliye" },
    { "@type": "AdministrativeArea", "name": "Eyyübiye" },
    ...neighborhoods.map(n => ({
      "@type": "AdministrativeArea",
      "name": `${n.name} Mahallesi`,
      "containedInPlace": { "@type": "AdministrativeArea", "name": "Karaköprü" }
    }))
  ],
  "availableLanguage": { "@type": "Language", "name": "Turkish", "alternateName": "tr" },
  "paymentAccepted": "Nakit, Kredi Kartı",
  "currenciesAccepted": "TRY",
  "priceRange": "$$",
  "image": absUrl('/og-image.jpg'),
  "logo": {
    "@type": "ImageObject",
    "@id": `${SITE_URL}/#logo`,
    "url": absUrl('/logo.png'),
    "contentUrl": absUrl('/logo.png'),
    "caption": SITE_NAME
  },
  "sameAs": [contactInfo.socialMedia.instagram].filter(Boolean)
}

export const webSiteData = {
  "@type": "WebSite",
  "@id": WEBSITE_ID,
  "url": SITE_URL,
  "name": SITE_NAME,
  "inLanguage": "tr-TR",
  "publisher": { "@id": ORG_ID }
}

/** Her sayfada kullanılacak temel gövde: kuruluş + site kimliği. */
export const baseGraph = [organizationData, webSiteData]

/**
 * Sayfa şemalarını tek bir @graph altında birleştirir.
 * Birden çok ayrı <script> yerine tek blok üretmek Google için daha güvenilirdir.
 */
export const buildGraph = (...nodes: Array<object | null | undefined>) => ({
  "@context": "https://schema.org",
  "@graph": [...baseGraph, ...nodes.filter(Boolean) as object[]]
})

export const breadcrumbListData = (items: Array<{ name: string; url: string }>) => ({
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": absUrl(item.url)
  }))
})

export const faqData = (faqs: Array<{ question: string; answer: string }>) => ({
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": { "@type": "Answer", "text": faq.answer }
  }))
})

/** Hizmet detay sayfaları için. */
export const serviceSchema = (params: {
  name: string
  description: string
  url: string
  image?: string
  serviceType?: string
}) => ({
  "@type": "Service",
  "@id": `${absUrl(params.url)}#service`,
  "name": params.name,
  "description": params.description,
  "url": absUrl(params.url),
  ...(params.image ? { "image": absUrl(params.image) } : {}),
  "serviceType": params.serviceType || params.name,
  "provider": { "@id": ORG_ID },
  "areaServed": { "@type": "City", "name": "Şanlıurfa" },
  "audience": { "@type": "Audience", "audienceType": "Evcil hayvan sahipleri" }
})

/**
 * Hastalık detay sayfaları için. Sağlık içeriği MedicalWebPage olarak
 * işaretlenir; hastalığın kendisi de about alanında yapılandırılır.
 */
export const medicalWebPageSchema = (params: {
  name: string
  description: string
  url: string
  symptoms?: string[]
  treatments?: string[]
  animal: 'dog' | 'cat'
}) => ({
  "@type": "MedicalWebPage",
  "@id": `${absUrl(params.url)}#webpage`,
  "name": params.name,
  "description": params.description,
  "url": absUrl(params.url),
  "inLanguage": "tr-TR",
  "isPartOf": { "@id": WEBSITE_ID },
  "publisher": { "@id": ORG_ID },
  "audience": { "@type": "Audience", "audienceType": "Evcil hayvan sahipleri" },
  "about": {
    "@type": "MedicalCondition",
    "name": params.name,
    "description": params.description,
    ...(params.symptoms?.length
      ? { "signOrSymptom": params.symptoms.map(s => ({ "@type": "MedicalSymptom", "name": s })) }
      : {}),
    ...(params.treatments?.length
      ? { "possibleTreatment": params.treatments.map(t => ({ "@type": "MedicalTherapy", "name": t })) }
      : {})
  },
  "keywords": params.animal === 'dog' ? 'köpek hastalıkları' : 'kedi hastalıkları'
})

/** Liste sayfaları (hizmetler, hastalıklar, ırklar) için. */
export const itemListSchema = (params: {
  name: string
  url: string
  items: Array<{ name: string; url: string }>
}) => ({
  "@type": "ItemList",
  "@id": `${absUrl(params.url)}#itemlist`,
  "name": params.name,
  "numberOfItems": params.items.length,
  "itemListElement": params.items.map((item, i) => ({
    "@type": "ListItem",
    "position": i + 1,
    "name": item.name,
    "url": absUrl(item.url)
  }))
})

/** Genel içerik sayfaları için (hakkımızda, iletişim, gizlilik vb.). */
export const webPageSchema = (params: {
  name: string
  description: string
  url: string
  type?: 'WebPage' | 'AboutPage' | 'ContactPage' | 'CollectionPage'
}) => ({
  "@type": params.type || "WebPage",
  "@id": `${absUrl(params.url)}#webpage`,
  "name": params.name,
  "description": params.description,
  "url": absUrl(params.url),
  "inLanguage": "tr-TR",
  "isPartOf": { "@id": WEBSITE_ID },
  "about": { "@id": ORG_ID }
})

/**
 * Geriye dönük uyumluluk: eski kodda localBusinessData import ediliyordu.
 * Artık organizationData ile aynı düğümü işaret eder.
 */
export const localBusinessData = organizationData

/**
 * Bir mahalleye özel hizmet sayfasının şeması.
 * Kliniğin kendisi ORG_ID ile referanslanır; her mahalle için ayrı bir
 * işletme nesnesi ÜRETİLMEZ (tek adresli klinik, tek LocalBusiness).
 */
export const neighborhoodServiceSchema = (params: {
  name: string
  slug: string
  lat: number
  lon: number
  description: string
  url: string
}) => ({
  "@type": "Service",
  "@id": `${absUrl(params.url)}#service`,
  "name": `${params.name} Mahallesi Veteriner Hizmeti`,
  "description": params.description,
  "serviceType": "Veteriner kliniği hizmeti",
  "provider": { "@id": ORG_ID },
  "areaServed": {
    "@type": "AdministrativeArea",
    "name": `${params.name} Mahallesi`,
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": params.lat,
      "longitude": params.lon
    },
    "containedInPlace": {
      "@type": "AdministrativeArea",
      "name": "Karaköprü",
      "containedInPlace": { "@type": "City", "name": "Şanlıurfa" }
    }
  },
  "url": absUrl(params.url)
})

/** Kedi/köpek gibi tür bazlı içerik merkezleri için koleksiyon şeması. */
export const collectionPageSchema = (params: {
  name: string
  description: string
  url: string
  items: Array<{ name: string; url: string }>
}) => ({
  "@type": "CollectionPage",
  "@id": `${absUrl(params.url)}#webpage`,
  "name": params.name,
  "description": params.description,
  "url": absUrl(params.url),
  "isPartOf": { "@id": WEBSITE_ID },
  "about": { "@id": ORG_ID },
  "mainEntity": {
    "@type": "ItemList",
    "itemListElement": params.items.map((it, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": it.name,
      "url": absUrl(it.url)
    }))
  }
})

/**
 * Aşı takvimi, kısırlaştırma gibi işlem/rehber sayfaları için.
 * Hastalık sayfalarından farklıdır: burada anlatılan bir MedicalCondition
 * değil, uygulanan bir MedicalProcedure'dür.
 */
export const procedureGuideSchema = (params: {
  name: string
  description: string
  url: string
  procedureType?: string
}) => ({
  "@type": "MedicalWebPage",
  "@id": `${absUrl(params.url)}#webpage`,
  "name": params.name,
  "description": params.description,
  "url": absUrl(params.url),
  "inLanguage": "tr-TR",
  "isPartOf": { "@id": WEBSITE_ID },
  "publisher": { "@id": ORG_ID },
  "audience": { "@type": "Audience", "audienceType": "Evcil hayvan sahipleri" },
  "about": {
    "@type": "MedicalProcedure",
    "name": params.name,
    "description": params.description,
    ...(params.procedureType ? { "procedureType": params.procedureType } : {})
  }
})
